/*
Copyright (C) 2020 Broadleaf Commerce.

Licensed under the Broadleaf End User License Agreement (EULA),
Version 1.1 (the “Commercial License” located at
http://license.broadleafcommerce.org/commercial_license-1.1.txt).

Alternatively, the Commercial License may be replaced with a mutually
agreed upon license (the “Custom License”) between you and
Broadleaf Commerce. You may not use this file except in compliance
with the applicable license.
*/
import React from 'react';
import { Line } from 'rc-progress';
import { get } from 'lodash';
import cx from 'classnames';

import AssetPreview from '../AssetPreview';

/**
 * Component that applies styling for an upload around an AssetPreview,
 * and handles the translation of the returned preview to the expected
 * asset keys.
 */
export const UploadPreview: React.FC<UploadPreviewProps> = ({
  className,
  isFailure,
  isSuccess,
  isUploading,
  preview,
  previewClassName,
  previewStyle,
  progress,
  srcKey = 'src',
  typeKey = 'type'
}) => (
  <div
    className={cx(
      'UploadPreview',
      'tw-relative tw-rounded tw-border-2 tw-border-transparent tw-transition-border tw-duration-500 tw-ease-in-out',
      {
        'tw-border-green-500': isSuccess,
        'tw-border-red-700': isFailure,
        'tw-border-blue-500': isUploading
      },
      className
    )}
  >
    {preview && (
      <AssetPreview
        className={cx(previewClassName, 'tw-h-full tw-w-full')}
        openInNewTab={false}
        src={get(preview, srcKey)}
        style={previewStyle}
        type={get(preview, typeKey)}
      />
    )}
    {isUploading && (
      <Line
        className="tw-absolute tw-bottom-4 tw-left-0 tw-right-0 tw-z-10 tw-mx-auto tw-w-4/5 tw-rounded-lg tw-border tw-border-secondary-900"
        strokeLinecap="square"
        prefixCls="UploadPreview"
        percent={progress}
        strokeWidth={6}
        trailWidth={6}
      />
    )}
  </div>
);

export interface UploadPreviewProps {
  /** A class name to be applied to the surrounding preview container */
  className?: string;
  /** A boolean used to apply styling upon failed upload */
  isFailure?: boolean;
  /** A boolean used to apply styling upon successful upload */
  isSuccess?: boolean;
  /** A boolean used to apply styling while uploading */
  isUploading?: boolean;
  preview?: object;
  /** optional className to be applied to the preview */
  previewClassName?: string;
  /** optional styling to be applied to the preview */
  previewStyle?: object;
  /** the progress in percentage points of the upload */
  progress?: number;
  /** The property key for getting an upload's src value */
  srcKey?: string;
  /** The property key for getting an upload's type value */
  typeKey?: string;
}

export default UploadPreview;
